import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Mail, Phone, User, DollarSign, Clock, Inbox, Sparkles } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function Empty({ icon: Icon, title, sub }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-14 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-brand-pink/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-brand-pink" />
      </div>
      <div className="font-display text-xl mb-1">{title}</div>
      <div className="text-white/60 text-sm">{sub}</div>
    </div>
  );
}

function BookingsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/coaching/bookings").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="text-white/50 p-10">Loading bookings…</div>;
  if (!rows.length) return <Empty icon={Inbox} title="No bookings yet." sub="When someone books a coaching call, it'll show up here." />;
  return (
    <div className="space-y-3" data-testid="admin-bookings-list">
      {rows.map((b) => (
        <div
          key={b.id}
          data-testid={`admin-booking-${b.id}`}
          className="rounded-2xl border border-white/10 bg-black/40 p-6 grid md:grid-cols-12 gap-4 items-start"
        >
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-brand-pink">{b.plan_name}</div>
            <div className="font-display text-xl mt-1">{b.name}</div>
            <div className="text-sm text-white/60 mt-2 space-y-1">
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {b.email}</div>
              {b.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {b.phone}</div>}
            </div>
          </div>
          <div className="md:col-span-3 text-sm text-white/70 space-y-1">
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-brand-pink" /> Preferred: {b.preferred_time || "flexible"}</div>
            <div className="flex items-center gap-2"><DollarSign className="w-3.5 h-3.5 text-brand-pink" /> ${b.amount}</div>
            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-brand-pink" /> {fmt(b.created_at)}</div>
          </div>
          <div className="md:col-span-5">
            {b.notes ? (
              <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4 text-sm text-white/80 leading-relaxed">
                <div className="text-[10px] uppercase tracking-widest text-brand-pink mb-1">Their goals</div>
                {b.notes}
              </div>
            ) : (
              <div className="text-white/40 text-sm italic">No notes provided.</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/leads").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="text-white/50 p-10">Loading leads…</div>;
  if (!rows.length) return <Empty icon={Sparkles} title="No leads yet." sub="Every email captured on the Home hero lead-magnet lands here." />;
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="admin-leads-list">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.04] text-white/60 text-left text-[11px] uppercase tracking-widest">
          <tr>
            <th className="p-4">Email</th>
            <th className="p-4">Name</th>
            <th className="p-4">Source</th>
            <th className="p-4">Welcome sent</th>
            <th className="p-4">Captured</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} data-testid={`admin-lead-${l.id}`} className="border-t border-white/5">
              <td className="p-4 text-white">{l.email}</td>
              <td className="p-4 text-white/70">{l.first_name || "—"}</td>
              <td className="p-4 text-white/70">{l.source || "—"}</td>
              <td className="p-4">
                {l.welcome_sent ? (
                  <span className="text-xs px-3 py-1 rounded-full bg-brand-pink/20 text-brand-pink">Sent</span>
                ) : (
                  <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/60">Pending</span>
                )}
              </td>
              <td className="p-4 text-white/60">{fmt(l.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/payments/transactions").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="text-white/50 p-10">Loading transactions…</div>;
  if (!rows.length) return <Empty icon={DollarSign} title="No transactions yet." sub="Once someone buys a course or product, orders show up here." />;
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="admin-transactions-list">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.04] text-white/60 text-left text-[11px] uppercase tracking-widest">
          <tr>
            <th className="p-4">Buyer</th>
            <th className="p-4">Item</th>
            <th className="p-4">Amount</th>
            <th className="p-4">Status</th>
            <th className="p-4">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.session_id} data-testid={`admin-tx-${t.session_id}`} className="border-t border-white/5">
              <td className="p-4 text-white">{t.user_email}</td>
              <td className="p-4 text-white/70">
                <div>{t.item_name}</div>
                <div className="text-[11px] text-white/40 uppercase tracking-widest">{t.item_type}</div>
              </td>
              <td className="p-4 text-white/80">${t.amount}</td>
              <td className="p-4">
                <span className={`text-xs px-3 py-1 rounded-full ${
                  t.payment_status === "paid" ? "bg-brand-pink/20 text-brand-pink"
                  : t.payment_status === "pending" ? "bg-white/10 text-white/60"
                  : "bg-red-500/20 text-red-300"
                }`}>{t.payment_status}</span>
              </td>
              <td className="p-4 text-white/60">{fmt(t.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminBookings() {
  const [tab, setTab] = useState("bookings");
  const TABS = [
    { id: "bookings", label: "Coaching Bookings", icon: Calendar },
    { id: "leads", label: "Email Leads", icon: Mail },
    { id: "transactions", label: "Transactions", icon: DollarSign },
  ];
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-2">Inbox</div>
          <h1 className="font-display text-4xl">Bookings & Leads</h1>
          <div className="text-white/60 text-sm mt-2">Everything humans are sending your way — in one place.</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-bookings-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition ${active ? "bg-brand-pink text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "bookings" && <BookingsTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "transactions" && <TransactionsTab />}
    </div>
  );
}
